import { createRoute, useNavigate } from '@tanstack/react-router'
import { adminRoute } from '../../route'
import { type FormEvent, useCallback, useEffect, useState } from 'react'
import { toORPCError } from '@orpc/client'
import { orpc } from '@/lib/orpc-client'
import { formatCredits, isOrgAdminRole } from '@hub/contracts'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AlertTriangle, ArrowLeft } from 'lucide-react'
import { OrgRoleInfoPopover } from '@/components/org-role-info-popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { GuestCheckbox } from '@/components/guest-checkbox'
import { Switch } from '@/components/ui/switch'
import { ProjectIcon } from '@/components/project-icon'

type AdminOrg = Awaited<ReturnType<typeof orpc.admin.getOrganization>>['organization']
type AdminOrgMember = Awaited<ReturnType<typeof orpc.admin.listOrgMembers>>['members'][number]
type AdminOrgProject = Awaited<ReturnType<typeof orpc.admin.listOrgProjects>>['projects'][number]

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB')
}

export const adminOrgDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/organizations/$orgId',
  component: AdminOrgDetail,
})

function AdminOrgDetail() {
  const { orgId } = adminOrgDetailRoute.useParams()
  const navigate = useNavigate()
  const [org, setOrg] = useState<AdminOrg | null>(null)
  const [members, setMembers] = useState<AdminOrgMember[]>([])
  const [projects, setProjects] = useState<AdminOrgProject[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  // Form state
  const [name, setName] = useState('')
  const [projectLimit, setProjectLimit] = useState(10)
  const [signupMode, setSignupMode] = useState<'invite_only' | 'domain_restricted'>('invite_only')
  const [allowedEmailDomain, setAllowedEmailDomain] = useState('')
  const [requiredAuthProvider, setRequiredAuthProvider] = useState<string>('')
  // Kept as strings: Number('') is 0, which would silently save a cleared
  // field as a real value (and 0 is not a legal credit limit).
  const [creditLimit, setCreditLimit] = useState('')
  const [creditsReset, setCreditsReset] = useState<'monthly' | 'manual'>('manual')
  const [creditsAnchorDay, setCreditsAnchorDay] = useState('')
  const [isResetting, setIsResetting] = useState(false)

  const loadOrg = useCallback(async () => {
    try {
      const [orgRes, membersRes, projectsRes] = await Promise.all([
        orpc.admin.getOrganization({ organizationId: orgId }),
        orpc.admin.listOrgMembers({ organizationId: orgId }),
        orpc.admin.listOrgProjects({ organizationId: orgId }),
      ])
      setOrg(orgRes.organization)
      setName(orgRes.organization.name)
      setProjectLimit(orgRes.organization.projectLimit)
      setSignupMode(orgRes.organization.signupMode)
      setAllowedEmailDomain(orgRes.organization.allowedEmailDomain ?? '')
      setRequiredAuthProvider(orgRes.organization.requiredAuthProvider ?? '')
      setCreditLimit(String(orgRes.organization.creditLimit))
      setCreditsReset(orgRes.organization.creditsReset)
      setCreditsAnchorDay(String(orgRes.organization.creditsAnchorDay))
      setMembers(membersRes.members)
      setProjects(projectsRes.projects)
    } catch {
      setError('Could not load organization.')
    } finally {
      setIsLoading(false)
    }
  }, [orgId])

  useEffect(() => {
    void loadOrg()
  }, [loadOrg])

  /** Whole number in [min, max], or null when the field is empty/invalid. */
  const wholeNumber = (raw: string, min: number, max: number): number | null => {
    const value = Number(raw.trim())
    if (raw.trim() === '' || !Number.isInteger(value) || value < min || value > max) return null
    return value
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setNotice(null)

    const limit = wholeNumber(creditLimit, 1, Number.MAX_SAFE_INTEGER)
    if (limit === null) {
      setError('Credit limit must be a whole number of at least 1.')
      return
    }
    const anchorDay = wholeNumber(creditsAnchorDay, 1, 28)
    if (creditsReset === 'monthly' && anchorDay === null) {
      setError('Anchor day must be a whole number between 1 and 28.')
      return
    }
    setIsSaving(true)

    try {
      const res = await orpc.admin.updateOrganization({
        organizationId: orgId,
        name: name.trim(),
        projectLimit,
        signupMode,
        allowedEmailDomain:
          signupMode === 'domain_restricted' ? allowedEmailDomain.trim() || null : null,
        requiredAuthProvider: (requiredAuthProvider || null) as 'google' | 'microsoft' | null,
        creditLimit: limit,
        creditsReset,
        ...(anchorDay !== null ? { creditsAnchorDay: anchorDay } : {}),
      })
      setOrg(res.organization)
      setNotice('Organization updated.')
    } catch {
      setError('Could not save. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleResetCredits = async () => {
    setError(null)
    setNotice(null)
    setIsResetting(true)
    try {
      const res = await orpc.admin.resetOrganizationCredits({ organizationId: orgId })
      setOrg(res.organization)
      setNotice('Credit usage reset — everything booked before now stops counting.')
    } catch {
      setError('Could not reset credit usage.')
    } finally {
      setIsResetting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    )
  }

  if (!org) {
    return (
      <div className="p-6">
        <p className="text-sm text-destructive">{error ?? 'Organization not found.'}</p>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-5xl space-y-6">
      <button
        onClick={() => void navigate({ to: '/admin' })}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      <Card>
        <CardHeader>
          <CardTitle>{org.name}</CardTitle>
          <CardDescription>
            Slug: {org.slug} · Prefix: {org.prefix ?? '–'} · {org.memberCount} members ·{' '}
            {org.projectCount} projects
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-5" onSubmit={(e) => void handleSave(e)}>
            <div className="space-y-2">
              <Label htmlFor="org-name">Name</Label>
              <Input
                id="org-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={100}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="org-limit">Project limit</Label>
              <Input
                id="org-limit"
                type="number"
                min={1}
                value={projectLimit}
                onChange={(e) => setProjectLimit(Number(e.target.value))}
                required
              />
            </div>

            <fieldset className="space-y-3">
              <legend className="text-sm font-medium text-foreground">Signup mode</legend>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="signupMode"
                  value="invite_only"
                  checked={signupMode === 'invite_only'}
                  onChange={() => setSignupMode('invite_only')}
                  className="accent-primary"
                />
                <span className="text-sm">Invite only</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="signupMode"
                  value="domain_restricted"
                  checked={signupMode === 'domain_restricted'}
                  onChange={() => setSignupMode('domain_restricted')}
                  className="accent-primary"
                />
                <span className="text-sm">Domain restricted</span>
              </label>
            </fieldset>

            {signupMode === 'domain_restricted' && (
              <div className="space-y-2">
                <Label htmlFor="org-domain">Allowed email domain</Label>
                <Input
                  id="org-domain"
                  placeholder="e.g. skanska.se"
                  value={allowedEmailDomain}
                  onChange={(e) => setAllowedEmailDomain(e.target.value)}
                />
              </div>
            )}

            <div className="grid gap-2 border-t border-border pt-5 mt-5">
              <Label htmlFor="org-auth-provider">Required sign-in method</Label>
              <select
                id="org-auth-provider"
                className="h-10 max-w-xs rounded-md border border-border bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-foreground/30"
                value={requiredAuthProvider}
                onChange={(e) => setRequiredAuthProvider(e.target.value)}
              >
                <option value="">None</option>
                <option value="google">Google</option>
                <option value="microsoft">Microsoft</option>
              </select>
              <p className="text-xs text-muted-foreground">
                This setting takes effect at the next sign-in.
              </p>
            </div>

            <div className="grid gap-4 border-t border-border pt-5 mt-5">
              <p className="text-sm font-medium text-foreground">Credits</p>
              <div className="space-y-2">
                <Label htmlFor="org-credit-limit">Credit limit</Label>
                <Input
                  id="org-credit-limit"
                  type="number"
                  min={1}
                  value={creditLimit}
                  onChange={(e) => setCreditLimit(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Adding credits = raising this number. Used this period:{' '}
                  <span className="tabular-nums">{formatCredits(org.creditsUsed, 'en-US')}</span>.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="org-credits-reset">Reset mode</Label>
                <select
                  id="org-credits-reset"
                  className="h-10 max-w-xs rounded-md border border-border bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-foreground/30"
                  value={creditsReset}
                  onChange={(e) => setCreditsReset(e.target.value as 'monthly' | 'manual')}
                >
                  <option value="manual">Manual — counts until reset</option>
                  <option value="monthly">Monthly — rolls over on the anchor day</option>
                </select>
                <p className="text-xs text-muted-foreground">
                  Periods are UTC, so an org in Stockholm rolls over at 01:00/02:00 local. Switching
                  to monthly starts counting from the current period immediately.
                </p>
              </div>

              {creditsReset === 'monthly' && (
                <div className="space-y-2">
                  <Label htmlFor="org-credits-anchor">Anchor day (1–28)</Label>
                  <Input
                    id="org-credits-anchor"
                    type="number"
                    min={1}
                    max={28}
                    value={creditsAnchorDay}
                    onChange={(e) => setCreditsAnchorDay(e.target.value)}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Current period ends{' '}
                    {org.creditsPeriodEnd
                      ? new Date(org.creditsPeriodEnd).toISOString().slice(0, 10)
                      : '–'}
                    .
                  </p>
                </div>
              )}

              <div>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isResetting}
                  onClick={() => void handleResetCredits()}
                >
                  {isResetting ? 'Resetting...' : 'Reset usage'}
                </Button>
              </div>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
            {notice && <p className="text-sm text-muted-foreground">{notice}</p>}

            <Button type="submit" disabled={isSaving || !name.trim()}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {org.memberCount === 0 && org.projectCount === 0 && (
        <OrgBootstrap
          org={org}
          onBootstrapped={(updated) => {
            setOrg(updated)
            void loadOrg()
          }}
        />
      )}

      <OrgActiveToggle org={org} onUpdated={setOrg} />

      <OrgFeatures org={org} onUpdated={setOrg} />

      {members.length > 0 && (
        <OrgMembersEditableTable
          orgId={orgId}
          members={members}
          onMembersChanged={() => void loadOrg()}
        />
      )}

      {projects.length > 0 && <OrgProjectsList projects={projects} />}
    </div>
  )
}

function OrgBootstrap({
  org,
  onBootstrapped,
}: {
  org: AdminOrg
  onBootstrapped: (org: AdminOrg) => void
}) {
  const [ownerEmail, setOwnerEmail] = useState('')
  const [projectName, setProjectName] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [bootstrapError, setBootstrapError] = useState<string | null>(null)

  const handleBootstrap = async (e: FormEvent) => {
    e.preventDefault()
    setBootstrapError(null)
    setIsSubmitting(true)
    try {
      const res = await orpc.admin.bootstrapOrg({
        organizationId: org.id,
        ownerEmail: ownerEmail.trim(),
        projectName: projectName.trim(),
      })
      onBootstrapped(res.organization)
    } catch (err) {
      const msg = toORPCError(err).message
      setBootstrapError(msg ?? 'Could not set up the organization.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Get started</CardTitle>
        <CardDescription>
          The organization has no members or projects. Invite an owner and create the first project.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => void handleBootstrap(e)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bootstrap-email">Owner email address</Label>
            <Input
              id="bootstrap-email"
              type="email"
              required
              placeholder="owner@company.com"
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              If the user does not exist, an account is created automatically. No invitation is sent
              — notify the owner manually.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="bootstrap-project">Project name</Label>
            <Input
              id="bootstrap-project"
              required
              placeholder="e.g. General"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              maxLength={100}
            />
          </div>
          {bootstrapError && <p className="text-sm text-destructive">{bootstrapError}</p>}
          <Button
            type="submit"
            disabled={isSubmitting || !ownerEmail.trim() || !projectName.trim()}
          >
            {isSubmitting ? 'Creating...' : 'Create owner and project'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

function OrgMembersEditableTable({
  orgId,
  members,
  onMembersChanged,
}: {
  orgId: string
  members: AdminOrgMember[]
  onMembersChanged: () => void
}) {
  const [savingUserId, setSavingUserId] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const ownerCount = members.filter((m) => m.role === 'owner').length

  const handleRoleChange = async (userId: string, role: string) => {
    setSaveError(null)
    setSavingUserId(userId)
    try {
      await orpc.admin.updateOrgMember({
        organizationId: orgId,
        userId,
        role: role as 'owner' | 'admin' | 'member',
      })
      onMembersChanged()
    } catch {
      setSaveError('Could not change role.')
    } finally {
      setSavingUserId(null)
    }
  }

  const handleToggleDisabled = async (userId: string, disabled: boolean) => {
    setSaveError(null)
    setSavingUserId(userId)
    try {
      await orpc.admin.toggleOrgMemberDisabled({
        organizationId: orgId,
        userId,
        disabled,
      })
      onMembersChanged()
    } catch {
      setSaveError('Could not change status.')
    } finally {
      setSavingUserId(null)
    }
  }

  const handleGuestChange = async (userId: string, isGuest: boolean) => {
    setSaveError(null)
    setSavingUserId(userId)
    try {
      await orpc.admin.updateOrgMember({
        organizationId: orgId,
        userId,
        isGuest,
      })
      onMembersChanged()
    } catch {
      setSaveError('Could not change guest status.')
    } finally {
      setSavingUserId(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Members</CardTitle>
      </CardHeader>
      <CardContent>
        {saveError && <p className="text-sm text-destructive mb-3">{saveError}</p>}
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">User</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  <span className="flex items-center gap-1">
                    Role
                    <OrgRoleInfoPopover />
                  </span>
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Guest</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  Last sign-in
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Joined</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground"></th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const isLastOwner = m.role === 'owner' && ownerCount < 2
                const isSaving = savingUserId === m.userId

                return (
                  <tr
                    key={m.userId}
                    className={`border-b border-border last:border-b-0${m.disabledAt ? ' opacity-50' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div>
                          <p className="font-medium text-foreground">{m.name ?? m.email}</p>
                          {m.name && <p className="text-xs text-muted-foreground">{m.email}</p>}
                        </div>
                        {m.disabledAt && (
                          <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                            Disabled
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div>
                              <select
                                className="h-8 rounded-md border border-border bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-foreground/30 disabled:opacity-50 disabled:cursor-not-allowed"
                                value={m.role}
                                onChange={(e) => void handleRoleChange(m.userId, e.target.value)}
                                disabled={isSaving || isLastOwner}
                              >
                                <option value="owner">Owner</option>
                                <option value="admin">Admin</option>
                                <option value="member">Member</option>
                              </select>
                            </div>
                          </TooltipTrigger>
                          {isLastOwner && (
                            <TooltipContent>
                              <p>Cannot change the role of the only owner</p>
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                    </td>
                    <td className="px-4 py-3">
                      <GuestCheckbox
                        checked={m.isGuest}
                        onCheckedChange={(checked) => void handleGuestChange(m.userId, checked)}
                        disabled={isSaving}
                        lockedReason={
                          isOrgAdminRole(m.role) ? 'Owners and admins cannot be guests' : null
                        }
                      />
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {m.lastLoginAt ? formatDate(m.lastLoginAt) : '–'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{formatDate(m.joinedAt)}</td>
                    <td className="px-4 py-3">
                      {m.role !== 'owner' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs"
                          disabled={isSaving}
                          onClick={() => void handleToggleDisabled(m.userId, !m.disabledAt)}
                        >
                          {m.disabledAt ? 'Enable' : 'Disable'}
                        </Button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

function OrgProjectsList({ projects }: { projects: AdminOrgProject[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Projects</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Name</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Slug</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Members</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Created</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} className="border-b border-border last:border-b-0">
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-2">
                      <ProjectIcon name={p.name} icon={p.icon} avatarUrl={null} size="sm" />
                      <div>
                        <p className="font-medium text-foreground">{p.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{p.id}</p>
                      </div>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{p.slug}</td>
                  <td className="px-4 py-3 text-right text-muted-foreground">{p.memberCount}</td>
                  <td className="px-4 py-3 text-muted-foreground">{formatDate(p.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

function OrgActiveToggle({
  org,
  onUpdated,
}: {
  org: AdminOrg
  onUpdated: (org: AdminOrg) => void
}) {
  const [isToggling, setIsToggling] = useState(false)
  const [toggleError, setToggleError] = useState<string | null>(null)
  const [showConfirm, setShowConfirm] = useState(false)

  const handleToggle = async () => {
    setToggleError(null)
    setIsToggling(true)
    try {
      const res = await orpc.admin.updateOrganization({
        organizationId: org.id,
        isActive: !org.isActive,
      })
      setShowConfirm(false)
      onUpdated(res.organization)
    } catch {
      setToggleError('Could not change status. Please try again.')
    } finally {
      setIsToggling(false)
    }
  }

  return (
    <>
      <Card className="border-destructive/30">
        <CardHeader>
          <CardTitle className="text-base">Organization status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {org.isActive ? (
            <>
              <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
                <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-destructive">Deactivate organization</p>
                  <p className="text-sm text-muted-foreground">
                    All {org.projectCount} projects in the organization will become unavailable.
                    Members will not be able to open or work in any project.
                  </p>
                </div>
              </div>
              <Button variant="destructive" onClick={() => setShowConfirm(true)}>
                Deactivate organization
              </Button>
            </>
          ) : (
            <>
              <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/50 p-4">
                <AlertTriangle className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">
                    The organization is deactivated
                  </p>
                  <p className="text-sm text-muted-foreground">
                    All projects are unavailable. Activate the organization to restore access.
                  </p>
                </div>
              </div>
              <Button onClick={() => void handleToggle()} disabled={isToggling}>
                {isToggling ? 'Activating...' : 'Activate organization'}
              </Button>
            </>
          )}
          {toggleError && <p className="text-sm text-destructive">{toggleError}</p>}
        </CardContent>
      </Card>

      <Dialog open={showConfirm} onOpenChange={(open) => !open && setShowConfirm(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate organization</DialogTitle>
            <DialogDescription>
              Are you sure you want to deactivate <strong>{org.name}</strong>? All{' '}
              {org.projectCount} projects will become unavailable and {org.memberCount} members will
              lose access.
            </DialogDescription>
          </DialogHeader>
          {toggleError && <p className="text-sm text-destructive">{toggleError}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)} disabled={isToggling}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => void handleToggle()} disabled={isToggling}>
              {isToggling ? 'Deactivating...' : 'Deactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/** Super-admin feature flags for the organization (every project inherits them). */
function OrgFeatures({ org, onUpdated }: { org: AdminOrg; onUpdated: (org: AdminOrg) => void }) {
  const [saving, setSaving] = useState<keyof AdminOrg['features'] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const toggle = async (key: keyof AdminOrg['features'], value: boolean) => {
    setSaving(key)
    setError(null)
    try {
      const res = await orpc.admin.updateOrganization({
        organizationId: org.id,
        features: { [key]: value },
      })
      onUpdated(res.organization)
    } catch {
      setError('Could not save the feature flag. Please try again.')
    } finally {
      setSaving(null)
    }
  }

  const rows: Array<{ key: keyof AdminOrg['features']; title: string; description: string }> = [
    {
      key: 'agentBrowser',
      title: 'Agent browser',
      description:
        'Lets HUB drive a real browser (websites, portals, forms) that users watch and take over in the chat workspace. Browser sessions are billed while open.',
    },
    {
      key: 'inbox',
      title: 'Project inbox',
      description:
        'Gives every project an email address. Incoming mail lands in an inbox where rules can hand it to HUB (reply, file attachments, create tasks). Hidden from the sidebar while off.',
    },
    {
      key: 'realtimeMeetings',
      title: 'Realtime meetings',
      description:
        'HUB-hosted video meetings started from the calendar: RealtimeKit media, live Whisper transcription and a rolling summary written into the meeting notes. Participant minutes and transcription are billed as credits.',
    },
    {
      key: 'mcpPlugins',
      title: 'Plugins (MCP)',
      description:
        'Project owners and admins can connect remote MCP servers in Settings › Plugins; HUB then calls their tools for every member of the project, as the account that authorised the plugin. Needs MCP_TOKEN_KEY on the Worker.',
    },
    {
      key: 'uiBlocks',
      title: 'UI blocks in chat',
      description:
        'HUB answers with interactive tables, charts and key figures instead of plain markdown tables, and the markdown tables in existing chats become sortable. Blocks already in a chat keep rendering if this is turned off.',
    },
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Features</CardTitle>
        <CardDescription>
          Optional capabilities for this organization. Changes apply on the next chat message.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {rows.map((row) => (
          <div key={row.key} className="flex items-start justify-between gap-6">
            <div>
              <p className="text-sm font-medium">{row.title}</p>
              <p className="text-sm text-muted-foreground">{row.description}</p>
            </div>
            <Switch
              checked={org.features[row.key]}
              disabled={saving !== null}
              aria-label={row.title}
              onCheckedChange={(next) => void toggle(row.key, next)}
            />
          </div>
        ))}
        {error && <p className="text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  )
}
